import { FC, ReactElement, useState } from "react";
import { Box, CircularProgress, Grid, Stack, Typography } from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import { Controller, useForm } from "react-hook-form";
import { PictureUploader, TextInput } from "@/components";
import { StyledButton } from "../styled";
import { useSnackbar, useUploadFile } from "@/hooks";
import { authApi, useUpdateUserInfoMutation } from "@/services";
import { roles } from "@/roles";
import { useAppDispatch } from "@/store";
import { ValidationPatterns } from "@/utils/form-validation";
import { tokens } from "@/tokens";

interface IUserDetails {
  firstName: string;
  lastName: string;
  email: string;
  phoneNo: string;
  profileImage: File | null;
}

interface IAccountFormProps {
  user: IUser | null;
}

const AccountForm: FC<IAccountFormProps> = ({ user }): ReactElement => {
  const isDisabled =
    user?.userRole !== roles.TDCLIENT && user?.userRole !== roles.SPOWNER;

  const [profilePreview, setProfilePreview] = useState<string | undefined>(
    user?.profileImage || undefined
  );
  const { isLoading, uploadFile } = useUploadFile();
  const [updateUserInfo, { isLoading: userInfoLoading }] =
    useUpdateUserInfoMutation();
  const dispatch = useAppDispatch();
  const { show } = useSnackbar();
  const { control, handleSubmit, setValue } = useForm<IUserDetails>({
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phoneNo: user?.phoneNo || "",
      profileImage: null,
    },
  });

  const handleProfileChange = (file: File | null) => {
    if (file) {
      const fileURL = URL.createObjectURL(file);
      setValue("profileImage", file);
      setProfilePreview(fileURL);
    }
  };
  const onSubmit = async (data: IUserDetails) => {
    const { profileImage, firstName, lastName } = data;
    try {
      let profileImgKey;
      if (profileImage) {
        const data = await uploadFile(profileImage);
        profileImgKey = data?.key || "";
      }

      await updateUserInfo({
        userData: {
          firstName,
          lastName,
          profileImage: profileImgKey,
        },
        userId: user?._id || "",
      }).unwrap();

      dispatch(authApi.util.invalidateTags([{ type: "CurrentUser" }]));

      show("User Information updated successfully!", "", "success");
    } catch (error: any) {
      show(error.message, "", "error");
    }
  };

  const isButtonLoading = isLoading || userInfoLoading;

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PictureUploader
        imgUrl={profilePreview}
        title="Profile Image"
        customPreviewType="circle"
        description="We recommend an image of at least 400x400."
        onChange={handleProfileChange}
        disabled={isDisabled}
      />
      <Grid
        container
        spacing={2}
        sx={{
          mt: 1.5,
          "& .MuiInputBase-root": {
            borderRadius: tokens.radius.md,
          },
          "& .MuiInputBase-root input": {
            px: 2,
            py: 1.25,
            '&[type="tel"]': {
              borderLeft: `1px solid ${tokens.border.default}`,
            },
          },
        }}
      >
        <Grid item xs={6}>
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: {
                value: true,
                message: "First Name is required.",
              },
            }}
            render={({ field: { onChange, ...rest } }) => (
              <TextInput
                {...rest}
                label="First Name"
                disabled={isDisabled}
                type="text"
                handleChangeValue={onChange}
                placeholder="Enter First name"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="lastName"
            control={control}
            rules={{
              required: {
                value: true,
                message: "Last Name is required.",
              },
            }}
            render={({ field: { onChange, ...rest } }) => (
              <TextInput
                {...rest}
                label="Last Name"
                disabled={isDisabled}
                handleChangeValue={onChange}
                type="text"
                placeholder="Enter Last name"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            name="email"
            control={control}
            rules={{
              pattern: {
                value: ValidationPatterns.email,
                message: "Invalid email address.",
              },
            }}
            render={({ field }) => (
              <TextInput
                label="Email Address"
                {...field}
                value={field.value}
                handleChangeValue={field.onChange}
                disabled
                type="email"
                placeholder="Enter email"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography
            sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
            fontWeight={tokens.fontWeight.semibold}
            fontSize={tokens.fontSize.lg}
            variant="caption"
          >
            Phone Number
          </Typography>
          <Controller
            name="phoneNo"
            control={control}
            render={({ field }) => (
              <MuiTelInput
                {...field}
                value={field.value}
                defaultCountry="US"
                disabled
                fullWidth
                onChange={field.onChange}
                placeholder="Enter Phone number"
              />
            )}
          />
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="flex-end" mt={2} spacing={2}>
        <StyledButton
          variant="contained"
          type="submit"
          disabled={isButtonLoading || isDisabled}
          {...(isButtonLoading && {
            startIcon: <CircularProgress sx={{ color: "white" }} size={20} />,
          })}
        >
          Save Changes
        </StyledButton>
      </Stack>
    </Box>
  );
};

export default AccountForm;
